import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const MailYonetimiPanel = () => {
  const [settings, setSettings] = useState(null);
  const [testEmail, setTestEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    axios.get(`${API}/mail-settings`, { withCredentials: true })
      .then(res => setSettings({
        scenarios: { gorev_atandiginda: true, gorev_durumu_degistiginde: true, gorev_son_tarih_yaklastiginda: false, gorev_son_tarih_gun: 3 },
        ...res.data,
      }))
      .catch(() => setMsg('❌ Mail ayarları yüklenemedi'));
  }, []);

  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(''), 5000); };

  const save = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/mail-settings`, settings, { withCredentials: true });
      flash('✅ Mail ayarları kaydedildi');
    } catch (e) { flash('❌ Kaydedilemedi: ' + (e.response?.data?.detail || e.message)); }
    finally { setSaving(false); }
  };

  const test = async () => {
    if (!testEmail) { flash('⚠️ Test için e-posta adresi girin'); return; }
    try {
      const res = await axios.post(`${API}/mail-test`, { email: testEmail }, { withCredentials: true });
      flash(res.data.success ? '✅ Test maili gönderildi' : '❌ Gönderilemedi: ' + (res.data.message || ''));
    } catch (e) { flash('❌ Test hatası: ' + (e.response?.data?.detail || e.message)); }
  };

  if (!settings) return <p className="text-gray-400">{msg || 'Mail ayarları yükleniyor…'}</p>;

  const SENARYOLAR = [
    { key: 'gorev_atandiginda', label: 'Görev atandığında', desc: 'Yeni görev atanan kişiye mail gider' },
    { key: 'gorev_durumu_degistiginde', label: 'Görev durumu değiştiğinde', desc: 'Durum güncellenince atanan kişiye mail gider' },
    { key: 'gorev_son_tarih_yaklastiginda', label: 'Son tarih yaklaştığında', desc: 'Hatırlatma maili gönderilir' },
  ];

  return (
    <div className="space-y-6" data-testid="mail-yonetim-panel">
      {msg && <div className="p-3 bg-gray-800 border border-indigo-700/50 rounded-lg text-sm text-white" data-testid="mail-panel-msg">{msg}</div>}
      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="bg-gray-900 border-gray-800">
          <CardHeader>
            <CardTitle className="text-white text-lg">Hesap ve Sunucu Ayarları</CardTitle>
            <CardDescription className="text-gray-500 text-xs">Görev bildirimlerinin gönderileceği mail hesabı (SMTP)</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-gray-300 text-xs">Gönderici Adı</Label>
                <Input value={settings.sender_name || ''} onChange={(e) => setSettings({ ...settings, sender_name: e.target.value })} className="bg-gray-800 border-gray-700 text-white text-sm h-9" placeholder="Kurumsal İş Yönetimi" data-testid="mail-sender-name-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-gray-300 text-xs">E-posta Adresi</Label>
                <Input value={settings.sender_email || settings.smtp_user || ''} onChange={(e) => setSettings({ ...settings, sender_email: e.target.value, smtp_user: e.target.value })} className="bg-gray-800 border-gray-700 text-white text-sm h-9" placeholder="istakip@cagatayturna.com" data-testid="mail-sender-email-input" />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-gray-300 text-xs">Şifre</Label>
              <Input type="password" value={settings.smtp_password || ''} onChange={(e) => setSettings({ ...settings, smtp_password: e.target.value })} className="bg-gray-800 border-gray-700 text-white text-sm h-9" placeholder="Mail hesap şifresi" data-testid="mail-password-input" />
            </div>
            <div className="grid grid-cols-5 gap-3 pt-2 border-t border-gray-800">
              <div className="col-span-2 space-y-1">
                <Label className="text-gray-300 text-xs">SMTP Sunucu</Label>
                <Input value={settings.smtp_host || ''} onChange={(e) => setSettings({ ...settings, smtp_host: e.target.value })} className="bg-gray-800 border-gray-700 text-white text-sm h-9" placeholder="smtp.cagatayturna.com" data-testid="mail-smtp-host-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-gray-300 text-xs">Port</Label>
                <Input type="number" value={settings.smtp_port || 587} onChange={(e) => setSettings({ ...settings, smtp_port: parseInt(e.target.value) || 587 })} className="bg-gray-800 border-gray-700 text-white text-sm h-9" />
              </div>
              <div className="col-span-2 space-y-1">
                <Label className="text-gray-300 text-xs">Şifreleme</Label>
                <Select value={settings.smtp_encryption || 'starttls'} onValueChange={(v) => {
                  const ports = { none: 25, starttls: 587, ssl: 465 };
                  setSettings({ ...settings, smtp_encryption: v, smtp_port: ports[v] || settings.smtp_port });
                }}>
                  <SelectTrigger className="bg-gray-800 border-gray-700 text-white h-9 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-700 text-white">
                    <SelectItem value="none">Yok (Port 25)</SelectItem>
                    <SelectItem value="starttls">STARTTLS (Port 587)</SelectItem>
                    <SelectItem value="ssl">SSL/TLS (Port 465)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-5 gap-3 pt-2 border-t border-gray-800">
              <div className="col-span-2 space-y-1">
                <Label className="text-gray-300 text-xs">IMAP Sunucu <span className="text-gray-600">(opsiyonel)</span></Label>
                <Input value={settings.imap_host || ''} onChange={(e) => setSettings({ ...settings, imap_host: e.target.value })} className="bg-gray-800 border-gray-700 text-white text-sm h-9" placeholder="imap.cagatayturna.com" />
              </div>
              <div className="space-y-1">
                <Label className="text-gray-300 text-xs">Port</Label>
                <Input type="number" value={settings.imap_port || 993} onChange={(e) => setSettings({ ...settings, imap_port: parseInt(e.target.value) || 993 })} className="bg-gray-800 border-gray-700 text-white text-sm h-9" />
              </div>
              <div className="col-span-2 space-y-1">
                <Label className="text-gray-300 text-xs">Şifreleme</Label>
                <Select value={settings.imap_encryption || 'ssl'} onValueChange={(v) => {
                  const ports = { none: 143, ssl: 993 };
                  setSettings({ ...settings, imap_encryption: v, imap_port: ports[v] || settings.imap_port });
                }}>
                  <SelectTrigger className="bg-gray-800 border-gray-700 text-white h-9 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-700 text-white">
                    <SelectItem value="none">Yok (Port 143)</SelectItem>
                    <SelectItem value="ssl">SSL/TLS (Port 993)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gray-900 border-gray-800">
          <CardHeader>
            <CardTitle className="text-white text-lg">Bildirimler ve Senaryolar</CardTitle>
            <CardDescription className="text-gray-500 text-xs">Hangi olaylarda mail gönderileceğini seçin</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex items-center justify-between py-2 border-b border-gray-800">
              <div>
                <p className="text-white text-sm font-medium">Mail Bildirimleri</p>
                <p className="text-gray-500 text-xs">Tüm mail gönderimini aç/kapat</p>
              </div>
              <Switch checked={!!settings.mail_enabled} onCheckedChange={(v) => setSettings({ ...settings, mail_enabled: v })} data-testid="mail-enabled-switch" />
            </div>
            {SENARYOLAR.map((s) => (
              <div key={s.key} className="flex items-center justify-between py-2 border-b border-gray-800 last:border-0">
                <div>
                  <p className="text-gray-200 text-sm">{s.label}</p>
                  <p className="text-gray-500 text-xs">{s.desc}</p>
                </div>
                <Switch checked={!!settings.scenarios?.[s.key]} onCheckedChange={(v) => setSettings({ ...settings, scenarios: { ...settings.scenarios, [s.key]: v } })} data-testid={`mail-senaryo-${s.key}`} />
              </div>
            ))}
            <div className="flex gap-2 pt-3 border-t border-gray-800">
              <Input value={testEmail} onChange={(e) => setTestEmail(e.target.value)} className="bg-gray-800 border-gray-700 text-white text-sm h-9" placeholder="Test: ornek@email.com" data-testid="mail-test-email-input" />
              <Button onClick={test} size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white h-9 px-4 flex-shrink-0" data-testid="mail-test-btn">Test Gönder</Button>
            </div>
          </CardContent>
        </Card>
      </div>
      <Button onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white px-8" data-testid="mail-kaydet-btn">{saving ? 'Kaydediliyor…' : 'Mail Ayarlarını Kaydet'}</Button>
    </div>
  );
};
